/**
 * №29 (S1) — ONE CONVERSATION, TWO HOMES.
 *
 * Mockup A puts the conversation on the stage; mockup B puts it in the rail. The
 * probe's own words: *"Make which-one-is-subject a user choice, not an architectural
 * one."* A user choice means the user can change their mind — so the move has to be
 * free, and "free" here has a precise, testable meaning:
 *
 *   **moving the conversation must not RE-CREATE it.**
 *
 * That is not decoration. `useV3ChatRuntime` holds its messages in `useChat`'s local
 * state, keyed to a `conversationId` minted on mount. Unmount the conversation and
 * every turn in it is gone — so a naive "the stage renders its own `<CoPanel/>`"
 * would mean promoting a live conversation to the stage wipes it. That is the exact
 * failure D-CH3 exists to prevent, one geometry up.
 *
 * ## The mechanism
 *
 * A single detached `<div>` — the HOST — is created once per shell. `WorkRail` always
 * renders the conversation into it through a React portal, and because the portal's
 * container never changes, React never reconciles the subtree away: one mount, for
 * the life of the shell (`__COPANEL_MOUNT_ID__` proves it).
 *
 * What moves is the host div itself, re-parented with `appendChild` into whichever
 * SLOT is currently claiming it:
 *
 *   stage slot  — a chat-subject route mounted `ChatSubjectPage` (mockup A)
 *   rail slot   — the work rail's Chat tab pane (mockup B), incl. while another tab
 *                 is in front (the pane is `hidden`, i.e. display:none, not unmounted)
 *   the park    — neither exists right now: the rail is collapsed, or the surface is
 *                 two-zone. Hidden, but still IN the document, so nothing is dropped.
 *
 * Re-parenting a DOM node preserves its subtree and React's ownership of it; only the
 * position in the document changes. This also fixes something that predates the
 * refactor: collapsing the right zone used to unmount the panel body and take a live
 * scope chat with it.
 *
 * The provider must sit ABOVE the shell (it is mounted in `AppShellRoot`), because the
 * park has to outlive every route.
 */
import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from "react";

/** Which slot wins when both exist. The stage is the subject; the rail is the aside. */
export type ConversationSlot = "stage" | "rail";

interface ConversationHostValue {
  /** The stable portal container. Rendered into by exactly one component (WorkRail). */
  host: HTMLElement | null;
  /** A slot registers its element (or null on unmount). Ref-callback shaped. */
  claim: (slot: ConversationSlot, el: HTMLElement | null) => void;
}

const Ctx = createContext<ConversationHostValue | null>(null);

export function ConversationHostProvider({ children }: { children: ReactNode }) {
  // `useState` with an initialiser, not `useMemo`: this element must be created
  // exactly once and must survive every re-render (useMemo is a cache, not a
  // guarantee). SSR-safe guard because the console renders client-side only but the
  // module is imported by tests that run in node.
  const [host] = useState<HTMLElement | null>(() => {
    if (typeof document === "undefined") return null;
    const el = document.createElement("div");
    // The host sits BETWEEN the slot and the conversation, so it has to pass the
    // slot's height through rather than shrink-wrapping the thread (which left the
    // composer floating mid-stage on the first render of the chat subject).
    el.className = "convohost";
    return el;
  });
  const [park, setPark] = useState<HTMLElement | null>(null);
  const slots = useRef<Record<ConversationSlot, HTMLElement | null>>({ stage: null, rail: null });

  /** Put the host wherever it belongs right now. Idempotent — `appendChild` on a node
   *  that is already the last child is a no-op move, which is what makes React 19's
   *  StrictMode double-invocation harmless here. */
  const place = useCallback(() => {
    if (!host) return;
    const target = slots.current.stage ?? slots.current.rail ?? park;
    if (target && host.parentElement !== target) target.appendChild(host);
  }, [host, park]);

  const claim = useCallback(
    (slot: ConversationSlot, el: HTMLElement | null) => {
      slots.current[slot] = el;
      place();
    },
    [place],
  );

  // The park is a slot too: registering it has to trigger a placement, otherwise the
  // very first paint (before any route mounts) leaves the host detached.
  const parkRef = useCallback(
    (el: HTMLElement | null) => {
      setPark(el);
      if (!host) return;
      const target = slots.current.stage ?? slots.current.rail ?? el;
      if (target && host.parentElement !== target) target.appendChild(host);
    },
    [host],
  );

  const value = useMemo<ConversationHostValue>(() => ({ host, claim }), [host, claim]);

  return (
    <Ctx.Provider value={value}>
      {children}
      {/* The park. `hidden` (display:none) rather than unmounted: a conversation the
          user has collapsed is out of sight, not out of existence. */}
      <div ref={parkRef} className="convopark" hidden aria-hidden="true" data-testid="convopark" />
    </Ctx.Provider>
  );
}

export function useConversationHost(): ConversationHostValue {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useConversationHost must be used within a ConversationHostProvider");
  return ctx;
}

/**
 * A ref callback that claims the conversation for this element while it is mounted
 * and releases it on unmount. `<div ref={useConversationSlot("stage")} />`.
 */
export function useConversationSlot(slot: ConversationSlot): (el: HTMLElement | null) => void {
  const { claim } = useConversationHost();
  return useCallback((el: HTMLElement | null) => claim(slot, el), [claim, slot]);
}
